import { useEffect, useRef } from "react";
import * as THREE from "three";
import type {
  ArchDefinition,
  ViewMode,
  PrescriptionItem,
  ToothAdjustmentMap,
} from "../../types/dental";


import {
  fitToView,
  zoomByFactor,
  setViewPreset,
} from "./viewportCameraUtils";


import type { ViewportCommand } from "../../types/dental";

import type { CrownSolveResult, ToothContext } from "../../types/restoration";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { buildCrownOverlayMesh } from "./viewport/restorationOverlay";

import { extractToothContextFromMesh } from "../../utils/restoration/toothContext";
import { RealisticToothDataset } from "../../services/tooth/RealisticToothDataset";

import {
  buildArchGroup,
  mergeToothCenters,  
  alignGroupXAxisToAxis,
} from "./viewport/archBuilder";

import {
  createViewportScene,
  createJawRig,
  createDebugLine,
  createDebugSphere,
  disposeScene,
} from "./viewport/sceneBuilders";

import {
  createToothInteractionState,
  disposeToothInteractionState,
  findToothIdFromIntersectionObject,
  refreshAllSelectionProxies,
  refreshSelectionProxyVisibility,
  registerToothObject as registerInteractiveToothObject,
} from "./toothViewportInteractions";


type GeometryDisplayMode = "blocks" | "realistic";

function worldOffsetToPivotLocal(
  worldOffset: THREE.Vector3,
  pivot: THREE.Object3D
): THREE.Vector3 {
  const inversePivotRotation = pivot.quaternion.clone().invert();
  return worldOffset.clone().applyQuaternion(inversePivotRotation);
}

interface DentalViewportProps {
  upper: ArchDefinition;
  lower: ArchDefinition;
  viewMode: ViewMode;
  selectedPrescriptionItem: PrescriptionItem | null;
  selectedToothId: string | null;
  onToothSelect?: (toothId: string | null) => void;
  focusNonce: number;
  toothAdjustments?: ToothAdjustmentMap;
  generatedCrownResults?: CrownSolveResult[];
  onRegisterToothContextProvider?: (
    provider: (toothId: string) => ToothContext | null
  ) => void;
  interactionMode?: "normal" | "pick_restoration_for_edit";
  onRestorationPickForEdit?: (planId: string) => void;
  geometryDisplayMode: GeometryDisplayMode;
  jawOpenAngleDeg: number;
  viewportCommand?: ViewportCommand | null;
}

export function DentalViewport({
  upper,
  lower,
  viewMode,
  selectedPrescriptionItem,
  selectedToothId,
  onToothSelect,
  focusNonce,
  toothAdjustments,
  generatedCrownResults = [],
  onRegisterToothContextProvider,
  interactionMode = "normal",
  onRestorationPickForEdit,
  geometryDisplayMode,
  jawOpenAngleDeg,
  viewportCommand,

}: DentalViewportProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
const controlsRef = useRef<OrbitControls | null>(null);
const anatomyGroupRef = useRef<THREE.Group | null>(null);
const restorationGroupRef = useRef<THREE.Group | null>(null);
  const suppressAutoFocusRef = useRef(false);
  const sceneBuildTokenRef = useRef(0);
  const lastViewDirectionRef = useRef<THREE.Vector3>(new THREE.Vector3(0, 0, 1));
  const toothInteractionStateRef = useRef<ReturnType<typeof createToothInteractionState> | null>(null);

  const cameraSnapshotRef = useRef<{
  position: THREE.Vector3;
  target: THREE.Vector3;
  up: THREE.Vector3;
} | null>(null);

const toothBaseTransformRef = useRef<
  Map<string, { position: THREE.Vector3; quaternion: THREE.Quaternion }>
>(new Map());

const upperJawPivotGroupRef = useRef<THREE.Group | null>(null);
const lowerJawPivotGroupRef = useRef<THREE.Group | null>(null);

const upperJawBaseQuaternionRef = useRef<THREE.Quaternion | null>(null);
const lowerJawBaseQuaternionRef = useRef<THREE.Quaternion | null>(null);


const applyJawOpenTransform = (
  angleDeg: number,
  selectedToothIdValue: string | null
) => {
  const upperPivot = upperJawPivotGroupRef.current;
  const lowerPivot = lowerJawPivotGroupRef.current;
  const upperBaseQuat = upperJawBaseQuaternionRef.current;
  const lowerBaseQuat = lowerJawBaseQuaternionRef.current;

  const angle = THREE.MathUtils.degToRad(angleDeg);

  if (upperPivot && upperBaseQuat) {
    upperPivot.quaternion.copy(upperBaseQuat);
    upperPivot.rotateX(-angle);
  }

  if (lowerPivot && lowerBaseQuat) {
    lowerPivot.quaternion.copy(lowerBaseQuat);
    lowerPivot.rotateX(angle);
  }

  const anatomyGroup = anatomyGroupRef.current;
  const state = toothInteractionStateRef.current;

  if (anatomyGroup) {
    anatomyGroup.updateWorldMatrix(true, true);
  }

  if (state && anatomyGroup) {
    refreshAllSelectionProxies(state, anatomyGroup);
    refreshSelectionProxyVisibility(state, selectedToothIdValue);
  }
};

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

        let isDisposed = false;
        const sceneBuildToken = ++sceneBuildTokenRef.current;

        console.log("[DentalViewport] scene build START", {
  sceneBuildToken,
  selectedPrescriptionItemId: selectedPrescriptionItem?.itemId,
  selectedToothId,
  focusNonce,
  viewMode,
  geometryDisplayMode,
  jawOpenAngleDeg,
  suppressAutoFocus: suppressAutoFocusRef.current,
});

      const previousCamera = cameraRef.current;
      const previousControls = controlsRef.current;

      if (previousCamera && previousControls) {
        cameraSnapshotRef.current = {
          position: previousCamera.position.clone(),
          target: previousControls.target.clone(),
          up: previousCamera.up.clone(),
        };
      }

      const {
        scene,
        camera,
        renderer,
        anatomyGroup,
        restorationGroup,
      } = createViewportScene(host);


      const controls = new OrbitControls(camera, renderer.domElement);

const cameraSnapshot = cameraSnapshotRef.current;

if (cameraSnapshot) {
  camera.position.copy(cameraSnapshot.position);
  camera.up.copy(cameraSnapshot.up);
  controls.target.copy(cameraSnapshot.target);
  camera.lookAt(controls.target);
  controls.update();
}



      cameraRef.current = camera;
      controlsRef.current = controls;
      anatomyGroupRef.current = anatomyGroup;
      restorationGroupRef.current = restorationGroup;

      controls.enableDamping = true;
      controls.enablePan = true;
      controls.screenSpacePanning = true;
      controls.minDistance = 0.01;
      controls.maxDistance = 5000;
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.PAN,
        RIGHT: THREE.MOUSE.PAN,
      };

      // 🔥 Track last manual camera direction (for Fit-to-View)
const handleControlsChange = () => {
  if (!cameraRef.current || !controlsRef.current) return;

  lastViewDirectionRef.current
    .copy(cameraRef.current.position)
    .sub(controlsRef.current.target)
    .normalize();
};

controls.addEventListener("change", handleControlsChange);


 
const {
  upperJawPivotGroup,
  upperJawContentGroup,
  lowerJawPivotGroup,
  lowerJawContentGroup,
} = createJawRig(anatomyGroup);

upperJawPivotGroupRef.current = upperJawPivotGroup;
lowerJawPivotGroupRef.current = lowerJawPivotGroup;
    

    const allToothCenters = new Map<string, THREE.Vector3>();

    let upperHingePoint: THREE.Vector3 | null = null;
let upperHingeAxis: THREE.Vector3 | null = null;
let lowerHingePoint: THREE.Vector3 | null = null;
let lowerHingeAxis: THREE.Vector3 | null = null;
    

const upperToothCenters = new Map<string, THREE.Vector3>();
const lowerToothCenters = new Map<string, THREE.Vector3>();

const toothInteractionState = createToothInteractionState();
toothInteractionStateRef.current = toothInteractionState;

const selectableRestorationObjects: THREE.Object3D[] = [];
const toothMeshMap = new Map<string, THREE.Mesh>();

    const applyHiddenBlockMaterial = (mesh: THREE.Mesh) => {
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];

      for (const entry of materials) {
        if (entry instanceof THREE.Material) {
          entry.transparent = true;
          entry.opacity = 0;
          entry.depthWrite = false;
        }
      }
    };

    const restoreBlockMaterial = (mesh: THREE.Mesh) => {
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];

      for (const entry of materials) {
        if (entry instanceof THREE.Material) {
          entry.transparent = false;
          entry.opacity = 1;
          entry.depthWrite = true;
        }
      }
    };

    const addBuiltArch = async (
      arch: ArchDefinition,
      color: number,
      yOffset: number,
      jaw: "upper" | "lower"
    ) => {
        const built = buildArchGroup(
          arch,
          color,
          yOffset,
          selectedPrescriptionItem,
          null,   // ❗ remove selection from build
          []
        );

      mergeToothCenters(allToothCenters, built.toothCenters);


      if (jaw === "upper") {
        upperHingePoint = built.hingePoint.clone();
        upperHingeAxis = built.hingeAxis.clone();
      } else {
        lowerHingePoint = built.hingePoint.clone();
        lowerHingeAxis = built.hingeAxis.clone();
      }


    if (jaw === "upper") {
      upperJawContentGroup.add(built.group);
     // mergeToothCenters(upperToothCenters, built.toothCenters);
    } else {
      lowerJawContentGroup.add(built.group);
     // mergeToothCenters(lowerToothCenters, built.toothCenters);
    }
     


      built.toothMeshes.forEach((mesh, toothId) => {
        toothMeshMap.set(toothId, mesh);
        registerInteractiveToothObject(
          toothInteractionState,
          anatomyGroup,
          toothId,
          mesh
        );

        toothBaseTransformRef.current.set(toothId, {
  position: mesh.position.clone(),
  quaternion: mesh.quaternion.clone(),
});
        

        if (geometryDisplayMode === "realistic") {
          applyHiddenBlockMaterial(mesh);
        } else {
          restoreBlockMaterial(mesh);
        }
      });

      if (geometryDisplayMode !== "realistic") {
        return;
      }

      const toothPromises = Array.from(built.toothMeshes.entries()).map(
        async ([toothId, sourceMesh]) => {
          try {
            const toothObject = await RealisticToothDataset.buildTooth({
  toothId,
  sourceMesh,
});

            if (isDisposed) {
              return;
            }

            if (jaw === "upper") {
              upperJawContentGroup.add(toothObject);
            } else {
              lowerJawContentGroup.add(toothObject);
            }

            registerInteractiveToothObject(
              toothInteractionState,
              anatomyGroup,
              toothId,
              toothObject
            );
            
            toothBaseTransformRef.current.set(toothId, {
  position: toothObject.position.clone(),
  quaternion: toothObject.quaternion.clone(),
});

          } catch (error) {
            console.error(`[ToothDataset] Failed to build tooth ${toothId}`, error);
          }
        }
      );

      await Promise.all(toothPromises);
      return built;
    };

   
const initializeScene = async () => {
  if (viewMode === "upper" || viewMode === "both") {
    await addBuiltArch(upper, 0x2563eb, 12, "upper");
  }

  if (viewMode === "lower" || viewMode === "both") {
    await addBuiltArch(lower, 0x16a34a, 0, "lower");
  }

  refreshSelectionProxyVisibility(toothInteractionState, selectedToothId);

  if (upperHingePoint && upperHingeAxis) {
    upperJawPivotGroup.position.copy(upperHingePoint);
    alignGroupXAxisToAxis(upperJawPivotGroup, upperHingeAxis);

    upperJawBaseQuaternionRef.current = upperJawPivotGroup.quaternion.clone();

    const upperWorldOffset = upperHingePoint.clone().multiplyScalar(-1);
    upperJawContentGroup.position.copy(
      worldOffsetToPivotLocal(upperWorldOffset, upperJawPivotGroup)
    );

  

    const debugLength = 120;
    const upperStart = upperHingePoint
      .clone()
      .add(upperHingeAxis.clone().multiplyScalar(-debugLength * 0.5));
    const upperEnd = upperHingePoint
      .clone()
      .add(upperHingeAxis.clone().multiplyScalar(debugLength * 0.5));

    scene.add(createDebugLine(upperStart, upperEnd, 0xff0000));
    scene.add(createDebugSphere(upperHingePoint, 0xff0000, 2));
  }

  if (lowerHingePoint && lowerHingeAxis) {
    lowerJawPivotGroup.position.copy(lowerHingePoint);
    alignGroupXAxisToAxis(lowerJawPivotGroup, lowerHingeAxis);
    lowerJawBaseQuaternionRef.current = lowerJawPivotGroup.quaternion.clone();

    const lowerWorldOffset = lowerHingePoint.clone().multiplyScalar(-1);
    lowerJawContentGroup.position.copy(
      worldOffsetToPivotLocal(lowerWorldOffset, lowerJawPivotGroup)
    );

    const debugLength = 120;
    const lowerStart = lowerHingePoint
      .clone()
      .add(lowerHingeAxis.clone().multiplyScalar(-debugLength * 0.5));
    const lowerEnd = lowerHingePoint
      .clone()
      .add(lowerHingeAxis.clone().multiplyScalar(debugLength * 0.5));

    scene.add(createDebugLine(lowerStart, lowerEnd, 0xffff00));
    scene.add(createDebugSphere(lowerHingePoint, 0xffff00, 2));
  }

  onRegisterToothContextProvider?.((toothId: string) => {
    const mesh = toothMeshMap.get(toothId);
    if (!mesh) {
      return null;
    }

    return extractToothContextFromMesh(mesh, toothId);
  });

  for (const result of generatedCrownResults) {
    if (!result?.success) {
      continue;
    }

    const crownOverlay = buildCrownOverlayMesh(result);
    if (!crownOverlay) {
      continue;
    }

    crownOverlay.userData = {
      ...crownOverlay.userData,
      type: "restoration",
      restorationType: result.metadata?.restorationType ?? "crown",
      planId: result.metadata?.planId,
      prescriptionItemId: result.metadata?.prescriptionItemId,
      targetToothId: result.metadata?.targetToothId,
    };

    restorationGroup.add(crownOverlay);
    selectableRestorationObjects.push(crownOverlay);
  }

  applyJawOpenTransform(jawOpenAngleDeg, selectedToothId);

  renderer.domElement.style.cursor =
    interactionMode === "pick_restoration_for_edit" ? "crosshair" : "default";
};

    void initializeScene();

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let pointerDownX = 0;
    let pointerDownY = 0;

    const getNormalizedPointer = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const onPointerDown = (event: PointerEvent) => {

      console.log("[DentalViewport] pointer DOWN", {
  cameraPosition: camera.position.toArray(),
  target: controls.target.toArray(),
});

      pointerDownX = event.clientX;
      pointerDownY = event.clientY;
    };

    const onPointerUp = (event: PointerEvent) => {

      console.log("[DentalViewport] pointer UP", {
  cameraPosition: camera.position.toArray(),
  target: controls.target.toArray(),
});

      const deltaX = Math.abs(event.clientX - pointerDownX);
      const deltaY = Math.abs(event.clientY - pointerDownY);

      if (deltaX > 4 || deltaY > 4) {
        return;
      }

    getNormalizedPointer(event);

    anatomyGroup.updateWorldMatrix(true, true);
    refreshAllSelectionProxies(toothInteractionState, anatomyGroup);
    refreshSelectionProxyVisibility(toothInteractionState, selectedToothId);

    raycaster.setFromCamera(pointer, camera);

      if (interactionMode === "pick_restoration_for_edit") {
        const restorationHits = raycaster.intersectObjects(
          selectableRestorationObjects,
          true
        );

        if (restorationHits.length === 0) {
          return;
        }

        let current: THREE.Object3D | null = restorationHits[0].object;

        while (current) {
          if (current.userData?.type === "restoration") {
            const planId = current.userData.planId as string | undefined;

            if (planId) {
              onRestorationPickForEdit?.(planId);
              return;
            }
          }

          current = current.parent;
        }

        return;
      }

    

      const intersections = raycaster.intersectObjects( toothInteractionState.selectableToothObjects, false );

      if (intersections.length === 0) {
        onToothSelect?.(null);
        return;
      }

   const toothId = findToothIdFromIntersectionObject(intersections[0].object);

if (!toothId) {
  onToothSelect?.(null);
  return;
}

onToothSelect?.(toothId);


    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointerup", onPointerUp);

    let animationFrameId = 0;

    const animate = () => {
      animationFrameId = window.requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    const onResize = () => {
      const nextWidth = host.clientWidth || 600;
      const nextHeight = host.clientHeight || 400;

      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(nextWidth, nextHeight);
    };

    window.addEventListener("resize", onResize);

return () => {
  isDisposed = true;

  window.cancelAnimationFrame(animationFrameId);
  window.removeEventListener("resize", onResize);

  renderer.domElement.removeEventListener("pointerdown", onPointerDown);
  renderer.domElement.removeEventListener("pointerup", onPointerUp);

  controls.removeEventListener("change", handleControlsChange);



// Preserve current camera before this scene is destroyed.
// This is critical when geometryDisplayMode changes.
cameraSnapshotRef.current = {
  position: camera.position.clone(),
  target: controls.target.clone(),
  up: camera.up.clone(),
};

controls.dispose();
renderer.dispose();

if (renderer.domElement.parentElement === host) {
  host.removeChild(renderer.domElement);
}

  disposeToothInteractionState(toothInteractionState, anatomyGroup);
  cameraRef.current = null;
  controlsRef.current = null;
  anatomyGroupRef.current = null;
  restorationGroupRef.current = null;

  upperJawPivotGroupRef.current = null;
  lowerJawPivotGroupRef.current = null;
  upperJawBaseQuaternionRef.current = null;
  lowerJawBaseQuaternionRef.current = null;

  disposeScene(scene);
};
  }, [
    upper,
    lower,
    viewMode,
    geometryDisplayMode,
    generatedCrownResults,
  ]);


  useEffect(() => {
  suppressAutoFocusRef.current = false;
}, [selectedPrescriptionItem, focusNonce]);


  useEffect(() => {
  if (!viewportCommand) {
    return;
  }

  const camera = cameraRef.current;
  const controls = controlsRef.current;
  const anatomyGroup = anatomyGroupRef.current;
  const restorationGroup = restorationGroupRef.current;

  if (!camera || !controls || !anatomyGroup || !restorationGroup) {
    console.warn("viewportCommand skipped: missing refs", {
      hasCamera: !!camera,
      hasControls: !!controls,
      hasAnatomy: !!anatomyGroup,
      hasRestoration: !!restorationGroup,
      viewportCommand,
    });
    return;
  }

const viewportObjects = [anatomyGroup, restorationGroup];

suppressAutoFocusRef.current = true;

console.log("[DentalViewport] viewportCommand BEFORE", {
  command: viewportCommand,
  cameraPosition: camera.position.toArray(),
  target: controls.target.toArray(),
  up: camera.up.toArray(),
  distance: camera.position.distanceTo(controls.target),
  lastViewDirection: lastViewDirectionRef.current.toArray(),
  suppressAutoFocus: suppressAutoFocusRef.current,
});

  switch (viewportCommand.type) {
case "fit":
  fitToView(
    camera,
    controls,
    viewportObjects,
    1.2,
    lastViewDirectionRef.current
  );
  break;

    case "zoomIn":
      zoomByFactor(camera, controls, 0.65, 0.01, 5000);
      break;

    case "zoomOut":
      zoomByFactor(camera, controls, 1.2, 0.01, 5000);
      break;

case "view": {
  const preset = viewportCommand.preset ?? "reset";

  switch (preset) {
    case "front":
      lastViewDirectionRef.current.set(0, 0, 1);
      break;
    case "back":
      lastViewDirectionRef.current.set(0, 0, -1);
      break;
    case "left":
      lastViewDirectionRef.current.set(-1, 0, 0);
      break;
    case "right":
      lastViewDirectionRef.current.set(1, 0, 0);
      break;
    case "top":
      lastViewDirectionRef.current.set(0, 1, 0);
      break;
    case "bottom":
      lastViewDirectionRef.current.set(0, -1, 0);
      break;
    default:
      lastViewDirectionRef.current
        .copy(camera.position)
        .sub(controls.target)
        .normalize();
      break;
  }

  setViewPreset(camera, controls, viewportObjects, preset);
  break;
}
      break;

    default:
      break;
  }

console.log("[DentalViewport] viewportCommand AFTER", {
  command: viewportCommand,
  cameraPosition: camera.position.toArray(),
  target: controls.target.toArray(),
  up: camera.up.toArray(),
  distance: camera.position.distanceTo(controls.target),
  lastViewDirection: lastViewDirectionRef.current.toArray(),
  suppressAutoFocus: suppressAutoFocusRef.current,
});
}, [viewportCommand]);

useEffect(() => {
  const state = toothInteractionStateRef.current;
  if (!state) return;

  refreshSelectionProxyVisibility(state, selectedToothId);
}, [selectedToothId]);

useEffect(() => {
  const state = toothInteractionStateRef.current;
  if (!state) return;

  state.toothObjectMap.forEach((rootObject, toothId) => {
    const baseTransform = toothBaseTransformRef.current.get(toothId);
    if (!baseTransform) return;

    rootObject.position.copy(baseTransform.position);
    rootObject.quaternion.copy(baseTransform.quaternion);

    const adjustment = toothAdjustments?.[toothId];
    if (!adjustment) return;

    const [rx, ry, rz] = adjustment.rotationDeg;
    const [tx, ty, tz] = adjustment.translation;

    const deltaEuler = new THREE.Euler(
      THREE.MathUtils.degToRad(rx),
      THREE.MathUtils.degToRad(ry),
      THREE.MathUtils.degToRad(rz),
      "XYZ"
    );

    const deltaQuat = new THREE.Quaternion().setFromEuler(deltaEuler);

    // Apply user rotation on top of original tooth orientation
    rootObject.quaternion.copy(baseTransform.quaternion).multiply(deltaQuat);

    // Apply movement on top of original local position
    rootObject.position.set(
      baseTransform.position.x + tx,
      baseTransform.position.y + ty,
      baseTransform.position.z + tz
    );

    // 🔑 IMPORTANT: ensure this object updates its world matrix
    rootObject.updateMatrix();
    rootObject.updateMatrixWorld(true);
  });

  // 🔑 CRITICAL FIX: refresh proxies after transforms
  const anatomyGroup = anatomyGroupRef.current;
  if (anatomyGroup) {
    anatomyGroup.updateMatrixWorld(true);

    refreshAllSelectionProxies(state, anatomyGroup);
    refreshSelectionProxyVisibility(state, selectedToothId);
  }

}, [toothAdjustments, selectedToothId]);

useEffect(() => {
  applyJawOpenTransform(jawOpenAngleDeg, selectedToothId);
}, [jawOpenAngleDeg, selectedToothId]);

  return <div ref={hostRef} className="dental-viewport-host" />;
}

function applyCameraFocus(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  selectedPrescriptionItem: PrescriptionItem | null,
  allToothCenters: Map<string, THREE.Vector3>
): void {

  console.log("[DentalViewport] applyCameraFocus CALLED", {
  hasSelectedPrescriptionItem: !!selectedPrescriptionItem,
  selectedPrescriptionItemId: selectedPrescriptionItem?.itemId,
  selectedTeeth: selectedPrescriptionItem?.target.teeth ?? [],
  cameraPositionBefore: camera.position.toArray(),
  targetBefore: controls.target.toArray(),});

  if (!selectedPrescriptionItem) {
    const defaultTarget = new THREE.Vector3(0, 10, 20);
    controls.target.copy(defaultTarget);
    camera.up.set(0, 1, 0);
    camera.position.set(0, 90, 180);
    camera.lookAt(defaultTarget);
    controls.update();
    return;
  }

  const targetPoints = selectedPrescriptionItem.target.teeth
    .map((toothId) => allToothCenters.get(toothId))
    .filter((point): point is THREE.Vector3 => Boolean(point));

  if (targetPoints.length === 0) {
    const defaultTarget = new THREE.Vector3(0, 10, 20);
    controls.target.copy(defaultTarget);
    camera.up.set(0, 1, 0);
    camera.position.set(0, 90, 180);
    camera.lookAt(defaultTarget);
    controls.update();
    return;
  }

  const focusCenter = new THREE.Vector3();
  for (const point of targetPoints) {
    focusCenter.add(point);
  }
  focusCenter.divideScalar(targetPoints.length);

  const spread = computeSpread(targetPoints);
  const distance = Math.max(55, 70 + spread * 1.4);

  const offset = new THREE.Vector3(0, distance * 0.45, distance);
  const nextCameraPosition = focusCenter.clone().add(offset);

  controls.target.copy(focusCenter);
  camera.up.set(0, 1, 0);
  camera.position.copy(nextCameraPosition);
  camera.lookAt(focusCenter);
  controls.update();

  console.log("[DentalViewport] applyCameraFocus END", {
  cameraPositionAfter: camera.position.toArray(),
  targetAfter: controls.target.toArray(),
});

}

function computeSpread(points: THREE.Vector3[]): number {
  if (points.length <= 1) {
    return 0;
  }

  let maxDistance = 0;

  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const distance = points[i].distanceTo(points[j]);
      if (distance > maxDistance) {
        maxDistance = distance;
      }
    }
  }

  return maxDistance;
}